import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaArrowRight } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  formatMoney,
  formatShortDate,
  customerName,
  paymentStatusColor,
} from './formatters';

const RecentOrdersCard = ({ orders, isLoading, delay = 0 }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const items = Array.isArray(orders) ? orders : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
    >
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-50">
            <FaShoppingCart className="h-4 w-4 text-green-600" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('dashboard.recentOrders.title')}
          </h2>
        </div>
        <button
          onClick={() => navigate('/orders')}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
        >
          {t('dashboard.recentOrders.viewAll')}
          <FaArrowRight className="text-[10px]" />
        </button>
      </div>

      <div className="flex-1">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse h-10 bg-gray-100 rounded" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {t('dashboard.recentOrders.empty')}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((order) => {
              const status = order?.status?.paymentStatus || 'pending';
              return (
                <li
                  key={order._id}
                  onClick={() => navigate('/orders')}
                  className="px-6 py-3 flex items-center gap-3 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        #{order.orderNumber}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatShortDate(order.createdAt)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {customerName(order)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 font-medium tabular-nums whitespace-nowrap">
                    {formatMoney(order?.totals?.totalAmount, order?.totals?.currency)}
                  </div>
                  <span
                    className={`px-2 py-0.5 inline-flex text-[11px] font-semibold rounded-full ${paymentStatusColor(status)}`}
                  >
                    {status}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </motion.div>
  );
};

export default RecentOrdersCard;
